import { Button, Col, Row, Typography } from "antd";
import React, { Fragment } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { DeleteIcon } from "../../../../../assets/svg";
import { Booking } from "../../../../../types/bookings";
import { SelectOption } from "../../../../../types/select";
import { InputField, ReactSelectField } from "../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../atoms/FormElement/InputNumberField";
import { getInputPlaceholderMessage, getRequiredErrorMessage } from "../../../../libs/form";
import { no_of_installments } from "../utils";
import { EditableField } from "./EditableField";

const { Text } = Typography;

type RentType = "areaSqft" | "fixed";

export const rentTypeOptions: SelectOption<RentType>[] = [
  { label: "Per Sq. Ft", value: "areaSqft" },
  { label: "Fixed Rent", value: "fixed" },
];

export default (props) => {
  const { fields, remove } = props;

  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { paymentPlanApproval, torType, propertyDetails } = (bookingDetails || {}) as Booking;

  const isPaymentPlanApprovalSent = paymentPlanApproval === "sent";
  const isTurnoverRentOnly = torType && torType === "turnoverRentOnly";
  const unitAreaInSqft = propertyDetails?.areaSqft;

  const { control, formState, setValue, getValues } = useFormContext();
  const { errors } = formState;

  const rentTypes = useWatch({ control, name: "plans" });
  const showUnitAreaAndRentValue = rentTypes?.some((plan) => plan?.rentType?.value === "areaSqft");

  return (
    <>
      <Row gutter={16} style={{ letterSpacing: "1.2px", marginBottom: "1rem" }}>
        <Col flex="4.5rem">
          <Text className="fontWeight600 ag-default mb-1"></Text>
        </Col>
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-2">NO. OF PAYMENTS</Text>
        </Col>
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-2">RENT TYPE</Text>
        </Col>
        {showUnitAreaAndRentValue && (
          <>
            <Col span={3}>
              <Text className="fontWeight600 ag-default mb-2">Unit Area (Sq.Ft)</Text>
            </Col>
            <Col span={3}>
              <Text className="fontWeight600 ag-default mb-2">RENT VALUE</Text>
            </Col>
          </>
        )}
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-2">RENT</Text>
        </Col>
        <Col span={4}>
          <Text className="fontWeight600 ag-default mb-2">SECURITY DEPOSITS</Text>
        </Col>
      </Row>
      {fields.map((item, index) => {
        const selectedRentType = getValues(`plans[${index}].rentType`);
        const isRentTypeSelected = Boolean(selectedRentType?.value);
        const error = errors?.plans?.[index];

        return (
          <Fragment key={item.id}>
            <Row gutter={10} style={{ position: "relative", marginBottom: isPaymentPlanApprovalSent ? "15px" : "0" }}>
              <Col flex="4.5rem">
                <div>
                  <Text className="fontWeight600 ag-default">Year {index + 1}</Text>
                  <InputField
                    classes="fm-input-hidden"
                    fieldname={`plans[${index}].noOfYear`}
                    control={control}
                    initValue={index + 1}
                    iProps={{ readOnly: true, type: "hidden" }}
                  />
                </div>
              </Col>
              <Col span={4}>
                <EditableField
                  isReadOnly={isPaymentPlanApprovalSent}
                  oldValue={item?.oldInstallments}
                  newValue={item?.installments}
                >
                  <ReactSelectField
                    isRequired
                    fieldname={`plans[${index}].installments`}
                    control={control}
                    iProps={{
                      placeholder: getInputPlaceholderMessage("no. of payments"),
                    }}
                    selectOption={no_of_installments}
                    rules={{ required: getRequiredErrorMessage("no. of payments") }}
                    validate={error?.installments && "error"}
                    validMessage={error?.installments?.message}
                  />
                </EditableField>
              </Col>
              <Col span={4}>
                <EditableField
                  isReadOnly={isPaymentPlanApprovalSent}
                  oldValue={item?.oldRentType}
                  newValue={item?.rentType}
                  options={rentTypeOptions}
                >
                  <ReactSelectField
                    isRequired
                    fieldname={`plans[${index}].rentType`}
                    initValue={item?.rentType}
                    control={control}
                    onChange={() => {
                      setValue(`plans[${index}].rentValue`, null);
                      setValue(`plans[${index}].annualRent`, null);
                    }}
                    iProps={{
                      placeholder: getInputPlaceholderMessage("rent type"),
                      isDisabled: isTurnoverRentOnly,
                    }}
                    selectOption={rentTypeOptions}
                    rules={{ required: getRequiredErrorMessage("rent type") }}
                    validate={error?.rentType && "error"}
                    validMessage={error?.rentType?.message}
                  />
                </EditableField>
              </Col>
              {showUnitAreaAndRentValue && (
                <>
                  {rentTypes?.[index]?.rentType?.value === "areaSqft" ? (
                    <>
                      <Col span={3}>
                        <EditableField
                          isReadOnly={isPaymentPlanApprovalSent}
                          oldValue={item?.oldUnitAreaInSqft}
                          newValue={item?.unitAreaInSqft}
                        >
                          <InputNumberField
                            control={control}
                            fieldname={`plans[${index}].unitAreaInSqft`}
                            defaultValue={item?.unitAreaInSqft || unitAreaInSqft || 1}
                            inputNumberProps={{ precision: 2 }}
                            disabled
                          />
                        </EditableField>
                      </Col>
                      <Col span={3}>
                        <EditableField
                          isReadOnly={isPaymentPlanApprovalSent}
                          oldValue={item?.oldRentValue}
                          newValue={item?.rentValue}
                        >
                          <InputNumberField
                            isRequired
                            fieldname={`plans[${index}].rentValue`}
                            control={control}
                            defaultValue={item?.rentValue}
                            onChange={(value) => {
                              const numericValue = Number(value || 0);
                              const unitAreaInSqft = getValues(`plans[${index}].unitAreaInSqft`);

                              const calculatedRent =
                                selectedRentType?.value === "areaSqft" && unitAreaInSqft
                                  ? numericValue * Number(unitAreaInSqft)
                                  : numericValue;

                              setValue(`plans[${index}].annualRent`, calculatedRent);
                            }}
                            disabled={!isRentTypeSelected}
                            inputNumberProps={{
                              placeholder: getInputPlaceholderMessage("rent value"),
                              min: 1,
                            }}
                            rules={{
                              required: getRequiredErrorMessage("rent value"),
                              min: { value: 1, message: "Rent value must be greater than 0" },
                            }}
                            format="currency"
                          />
                        </EditableField>
                      </Col>
                    </>
                  ) : (
                    <Col span={6} />
                  )}
                </>
              )}

              <Col span={4}>
                <EditableField
                  isReadOnly={isPaymentPlanApprovalSent}
                  oldValue={item?.oldAnnualRent}
                  newValue={item?.annualRent}
                >
                  <InputNumberField
                    isRequired
                    fieldname={`plans[${index}].annualRent`}
                    inputNumberProps={{
                      placeholder:
                        selectedRentType?.value === "areaSqft"
                          ? getInputPlaceholderMessage("rent value to calculate rent")
                          : getInputPlaceholderMessage("rent"),
                    }}
                    disabled={isTurnoverRentOnly || !isRentTypeSelected || selectedRentType?.value === "areaSqft"}
                    control={control}
                    defaultValue={item?.annualRent}
                    rules={{
                      required: getRequiredErrorMessage("rent"),
                      min: isTurnoverRentOnly
                        ? { value: 0, message: "Rent must be greater or equal to 0" }
                        : { value: 1, message: "Rent must be greater than 0" },
                    }}
                    format="currency"
                  />
                </EditableField>
              </Col>
              <Col span={4}>
                {index < 1 && (
                  <EditableField
                    isReadOnly={isPaymentPlanApprovalSent}
                    oldValue={item?.oldSecurityDeposit}
                    newValue={item?.securityDeposit}
                  >
                    <InputNumberField
                      isRequired
                      fieldname={`plans[${index}].securityDeposit`}
                      inputNumberProps={{
                        placeholder: getInputPlaceholderMessage("security deposit"),
                      }}
                      control={control}
                      defaultValue={item?.securityDeposit}
                      rules={{
                        required: getRequiredErrorMessage("security deposit"),
                        min: { value: 0, message: "Security deposit must be greater or equal to 0" },
                      }}
                      format="currency"
                    />
                  </EditableField>
                )}
              </Col>
              {!isPaymentPlanApprovalSent && !isTurnoverRentOnly && (
                <Col flex="auto">
                  <Button type="link" icon={<DeleteIcon />} onClick={() => remove(index)} />
                </Col>
              )}
            </Row>
          </Fragment>
        );
      })}
    </>
  );
};
